import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import AuthModal from './AuthModal';
import * as syncService from '../services/sync/syncService';
import { getAppData, saveAppData } from '../lib/storage';
import { createBackup } from '../services/storage/backupService';

interface MobileAuthSyncProps {
  onClose: () => void;
}

const MobileAuthSync: React.FC<MobileAuthSyncProps> = ({ onClose }) => {
  const { user, profile, signOut } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'uploading' | 'downloading' | 'success' | 'error'>('idle');
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // 移除自动刷新逻辑，React Query 会自动处理 profile 的加载
  // 如果 user 存在但 profile 为 null，React Query 会自动触发 useUserProfile 查询

  // 加载最后同步时间
  useEffect(() => {
    const loadLastSyncTime = async () => {
      const time = await syncService.getLastSyncTime();
      setLastSyncTime(time);
    };

    if (user) {
      loadLastSyncTime();
    }
  }, [user]);

  // 格式化时间
  const formatTime = (date: Date | null): string => {
    if (!date) return '从未同步';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes} 分钟前`;
    if (hours < 24) return `${hours} 小时前`;
    if (days < 7) return `${days} 天前`;
    return date.toLocaleDateString('zh-CN');
  };

  // 上传数据
  const handleUpload = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      setSyncStatus('uploading');
      setSyncMessage('正在上传数据...');

      const appData = await getAppData();
      const { error: uploadError } = await syncService.uploadData(appData);
      
      if (uploadError) {
        throw new Error(uploadError.message || '上传失败');
      }

      const now = new Date();
      setLastSyncTime(now);
      setSyncStatus('success');
      setSyncMessage('上传成功');
      
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage(null);
      }, 2000);
    } catch (error: any) {
      console.error('上传失败:', error);
      setSyncStatus('error');
      setSyncMessage(error.message || '上传失败，请重试');
      
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage(null);
      }, 3000);
    }
  };

  // 下载数据
  const handleDownload = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      setSyncStatus('downloading');
      setSyncMessage('正在下载数据...');

      // 使用智能合并下载
      const { data: mergedData, error: downloadError } = await syncService.downloadData(true);
      if (downloadError) {
        setSyncStatus('error');
        setSyncMessage(downloadError.message || '下载失败，请重试');
        
        setTimeout(() => {
          setSyncStatus('idle');
          setSyncMessage(null);
        }, 3000);
        return;
      }

      if (mergedData) {
        // 在合并前创建备份
        try {
          const localData = await getAppData();
          await createBackup(localData);
          console.log('[Sync] 已创建本地数据备份');
        } catch (backupError) {
          console.warn('[Sync] 创建备份失败:', backupError);
          // 备份失败不影响下载流程，但记录警告
        }

        // 保存合并后的数据（skipAutoSync=true，因为这是从云端下载的）
        await saveAppData(mergedData, true);
        setSyncStatus('success');
        setSyncMessage('下载成功，数据已智能合并');
        
        // 刷新页面以显示新数据
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setSyncStatus('error');
        setSyncMessage('没有可下载的数据');
        
        setTimeout(() => {
          setSyncStatus('idle');
          setSyncMessage(null);
        }, 2000);
      }
    } catch (error: any) {
      console.error('下载失败:', error);
      setSyncStatus('error');
      setSyncMessage(error.message || '下载失败，请重试');
      
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage(null);
      }, 3000);
    }
  };

  // 登出
  const handleSignOut = async () => {
    try {
      await signOut();
      setLastSyncTime(null);
    } catch (error) {
      console.error('登出失败:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center">
      <div className="bg-white dark:bg-gray-800 w-full max-w-md max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-2xl shadow-xl">
        {/* 头部 */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            账号与同步
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 内容 */}
        <div className="p-4 space-y-4">
          {!user ? (
            // 未登录状态
            <div className="text-center py-8">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                登录后可以在多设备间同步数据
              </p>
              <button
                onClick={() => setShowAuthModal(true)}
                className="w-full px-4 py-3 bg-ios-blue text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
              >
                登录
              </button>
            </div>
          ) : (
            // 已登录状态
            <>
              {/* 用户信息 */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-ios-blue flex items-center justify-center text-white font-semibold text-lg">
                    {profile?.username?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {profile?.username || user.email || '用户'}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {user.email}
                    </div>
                  </div>
                </div>
              </div>

              {/* 同步状态 */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    最后同步时间
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {formatTime(lastSyncTime)}
                  </span>
                </div>
                
                {syncMessage && (
                  <div className={`text-sm py-2 px-3 rounded-lg mb-3 ${
                    syncStatus === 'success' 
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : syncStatus === 'error'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                  }`}>
                    {syncMessage}
                  </div>
                )}
              </div>

              {/* 同步操作 */}
              <div className="space-y-2">
                <button
                  onClick={handleUpload}
                  disabled={syncStatus === 'uploading' || syncStatus === 'downloading'}
                  className="w-full px-4 py-3 bg-ios-blue text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {syncStatus === 'uploading' ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      上传中...
                    </>
                  ) : (
                    '上传数据'
                  )}
                </button>

                <button
                  onClick={handleDownload}
                  disabled={syncStatus === 'uploading' || syncStatus === 'downloading'}
                  className="w-full px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {syncStatus === 'downloading' ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      下载中...
                    </>
                  ) : (
                    '下载数据'
                  )}
                </button>

                <button
                  onClick={handleSignOut}
                  className="w-full px-4 py-3 text-red-600 dark:text-red-400 rounded-lg font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  登出
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 登录模态框 */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            // React Query 会自动处理用户信息的更新，不需要手动刷新
          }}
        />
      )}
    </div>
  );
};

export default MobileAuthSync;
